import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { apis } from '../../../core/services/params';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SimButton {
    id: string;
    title: string;
}

interface SimSectionRow {
    id: string;
    title: string;
    description?: string;
}

interface SimSection {
    title?: string;
    rows: SimSectionRow[];
}

interface BotMessage {
    text: string;
    buttons?: SimButton[];
    // WhatsApp List Message fields
    button?: string;       // CTA label e.g. "Select"
    sections?: SimSection[];
}

interface ChatEntry {
    role: 'user' | 'bot';
    text: string;
    buttons?: SimButton[];
    // List message
    listButton?: string;
    sections?: SimSection[];
    timestamp: Date;
    buttonLabel?: string;
}

interface RfqCareSimulatorProps {
    visible: boolean;
    onClose: () => void;
}

// ─── SVG helpers ──────────────────────────────────────────────────────────────

const WaIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787" />
    </svg>
);

const DoubleTickIcon = () => (
    <svg viewBox="0 0 16 11" fill="currentColor" className="w-3.5 h-2.5 text-blue-300 inline-block ml-1">
        <path d="M11.071.653a.375.375 0 0 0-.531.02L5.85 5.765 3.84 3.493a.375.375 0 1 0-.567.492l2.302 2.605a.375.375 0 0 0 .549.014L11.09 1.18a.375.375 0 0 0-.019-.528zM15.071.653a.375.375 0 0 0-.531.02L9.85 5.765l-.47-.531a.375.375 0 1 0-.566.492l.75.849a.375.375 0 0 0 .548.014L15.09 1.18a.375.375 0 0 0-.019-.528zM.379 6.1a.375.375 0 0 0 0 .75h1.5a.375.375 0 0 0 0-.75H.379z" />
    </svg>
);

const SendIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Render WhatsApp-style bold (*text*) and newlines */
const renderWhatsAppText = (text: string) => {
    const parts = text.split(/(\*[^*]+\*)/g);
    return parts.map((part, i) => {
        if (part.startsWith('*') && part.endsWith('*')) {
            return <strong key={i}>{part.slice(1, -1)}</strong>;
        }
        return <span key={i}>{part}</span>;
    });
};

const formatTime = (d: Date) =>
    d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });

// ─── WhatsApp wallpaper SVG pattern ──────────────────────────────────────────
const WA_BG = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c8b8a0' fill-opacity='0.18'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

// ─── Component ────────────────────────────────────────────────────────────────

const RfqCareSimulator: React.FC<RfqCareSimulatorProps> = ({ visible, onClose }) => {
    const [userId, setUserId] = useState('');
    const [draftUserId, setDraftUserId] = useState('');
    const [sessionStarted, setSessionStarted] = useState(false);
    const [chat, setChat] = useState<ChatEntry[]>([]);
    const [inputText, setInputText] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Index of the chat entry whose list picker is open (-1 = none)
    const [openListIdx, setOpenListIdx] = useState(-1);
    const bottomRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Reset when modal closes
    useEffect(() => {
        if (!visible) {
            setUserId('');
            setDraftUserId('');
            setSessionStarted(false);
            setChat([]);
            setInputText('');
            setError(null);
            setSending(false);
            setOpenListIdx(-1);
        }
    }, [visible]);

    // Scroll to bottom on new messages
    useEffect(() => {
        if (!sending) {
            setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
        }
    }, [chat, sending]);

    // Focus input after session starts
    useEffect(() => {
        if (sessionStarted) setTimeout(() => inputRef.current?.focus(), 150);
    }, [sessionStarted]);

    if (!visible) return null;

    const startSession = () => {
        const trimmed = draftUserId.trim();
        if (!trimmed) return;
        setUserId(trimmed);
        setSessionStarted(true);
        setChat([]);
        setError(null);
    };

    const sendMessage = async (messageText: string, messageType: 'text' | 'button' = 'text', buttonLabel?: string) => {
        if (!messageText.trim() || sending) return;
        setError(null);
        setSending(true);

        // Push user entry immediately
        const userEntry: ChatEntry = {
            role: 'user',
            text: messageText,
            timestamp: new Date(),
            buttonLabel: buttonLabel,
        };
        setChat(prev => [...prev, userEntry]);
        setInputText('');

        try {
            const res = await axios.post(apis.RFQ_CARE_SIMULATE_MESSAGE, {
                user_id: userId,
                message: messageText,
                message_type: messageType,
            });

            const data = res.data?.data;
            if (data?.messages && Array.isArray(data.messages)) {
                const botEntries: ChatEntry[] = (data.messages as BotMessage[]).map(m => ({
                    role: 'bot',
                    text: m.text || '',
                    buttons: m.buttons || [],
                    listButton: m.button,
                    sections: m.sections,
                    timestamp: new Date(),
                }));
                setChat(prev => [...prev, ...botEntries]);
            }
        } catch (e: any) {
            const msg = e?.response?.data?.message || e?.message || 'Something went wrong';
            setError(msg);
            // Add error bubble
            setChat(prev => [...prev, {
                role: 'bot',
                text: `⚠️ Error: ${msg}`,
                timestamp: new Date(),
            }]);
        } finally {
            setSending(false);
        }
    };

    const handleSendText = () => {
        if (inputText.trim()) sendMessage(inputText.trim(), 'text');
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendText();
        }
    };

    const handleButtonClick = (btn: SimButton) => {
        sendMessage(btn.id, 'button', btn.title);
    };

    const handleListRowClick = (row: SimSectionRow) => {
        setOpenListIdx(-1);
        sendMessage(row.id, 'list_reply' as any, row.title);
    };

    return (
        <div
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            style={{ background: 'rgba(0,0,0,0.75)' }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="flex flex-col rounded-2xl overflow-hidden shadow-2xl"
                style={{ width: 440, maxHeight: '90vh', minHeight: 560, border: '1.5px solid rgba(255,255,255,0.08)' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* ── WhatsApp Header ── */}
                <div
                    className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
                    style={{ background: '#075E54' }}
                >
                    <button
                        onClick={onClose}
                        className="text-white opacity-70 hover:opacity-100 transition-opacity mr-1 flex-shrink-0"
                        title="Close"
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    {/* Avatar */}
                    <div
                        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-white"
                        style={{ background: '#128C7E' }}
                    >
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                            <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                        </svg>
                    </div>
                    {/* Name area */}
                    <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold text-sm leading-tight truncate">
                            {sessionStarted ? `+91 ${userId}` : 'RFQ Care Simulator'}
                        </p>
                        <p className="text-green-200 text-[11px] mt-0.5 flex items-center gap-1">
                            {sessionStarted ? (
                                sending ? (
                                    <span className="flex items-center gap-1">
                                        <span className="inline-block w-1.5 h-1.5 bg-green-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                        <span className="inline-block w-1.5 h-1.5 bg-green-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                        <span className="inline-block w-1.5 h-1.5 bg-green-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                        <span className="ml-0.5">typing…</span>
                                    </span>
                                ) : 'online'
                            ) : 'Customer Simulator'}
                        </p>
                    </div>
                    <span className="text-green-200 opacity-60 flex-shrink-0"><WaIcon /></span>
                </div>

                {/* ── Chat area (wallpaper) ── */}
                <div
                    className="flex-1 overflow-y-auto px-3 py-3 space-y-1"
                    style={{ background: '#ECE5DD', backgroundImage: WA_BG }}
                >
                    {!sessionStarted ? (
                        /* ── Session start screen ── */
                        <div className="flex flex-col items-center justify-center h-full gap-4 py-12">
                            <div className="w-16 h-16 rounded-full flex items-center justify-center text-white" style={{ background: '#25D366' }}>
                                <WaIcon />
                            </div>
                            <div className="text-center">
                                <p className="text-gray-700 font-semibold text-base">RFQ Care Simulator</p>
                                <p className="text-gray-500 text-xs mt-1 max-w-[260px] text-center">
                                    Enter a customer phone number to simulate a WhatsApp conversation with the RFQ Care bot.
                                </p>
                            </div>
                            <div className="w-full max-w-[300px] bg-white rounded-xl shadow-sm p-4 flex flex-col gap-3">
                                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Customer Phone / User ID</label>
                                <input
                                    type="text"
                                    placeholder="e.g. 9415332242"
                                    value={draftUserId}
                                    onChange={e => setDraftUserId(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') startSession(); }}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent"
                                    autoFocus
                                />
                                <button
                                    onClick={startSession}
                                    disabled={!draftUserId.trim()}
                                    className="w-full py-2 rounded-lg text-white font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ background: draftUserId.trim() ? '#25D366' : '#a8d5b5' }}
                                >
                                    Start Conversation
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Date chip */}
                            <div className="flex justify-center my-2">
                                <span className="bg-white/70 text-gray-500 text-[10px] px-3 py-0.5 rounded-full shadow-sm">
                                    Today
                                </span>
                            </div>

                            {/* System hint chip */}
                            <div className="flex justify-center mb-3">
                                <span className="bg-[#FFF3CD] text-[#856404] text-[11px] px-3 py-1 rounded-lg shadow-sm text-center max-w-[280px]">
                                    🤖 Simulating as <strong>{userId}</strong> — type a message or tap a button reply
                                </span>
                            </div>

                            {/* Messages */}
                            {chat.map((entry, idx) => {
                                const isUser = entry.role === 'user';
                                return (
                                    <div key={idx} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-0.5`}>
                                        <div
                                            className="relative max-w-[82%] px-3 py-2 rounded-lg shadow-sm text-[13px] leading-[1.5]"
                                            style={{
                                                background: isUser ? '#DCF8C6' : '#FFFFFF',
                                                borderRadius: isUser ? '12px 2px 12px 12px' : '2px 12px 12px 12px',
                                            }}
                                        >
                                            {/* Bubble tail */}
                                            <span style={{
                                                position: 'absolute',
                                                top: 0,
                                                [isUser ? 'right' : 'left']: -6,
                                                width: 0,
                                                height: 0,
                                                borderStyle: 'solid',
                                                borderWidth: isUser ? '0 0 8px 8px' : '0 8px 8px 0',
                                                borderColor: isUser
                                                    ? 'transparent transparent transparent #DCF8C6'
                                                    : 'transparent #FFFFFF transparent transparent',
                                            }} />

                                            {/* User button-click pill label */}
                                            {isUser && entry.buttonLabel && (
                                                <span className="block text-[10px] font-semibold text-green-700 mb-0.5 uppercase tracking-wide">
                                                    Button: {entry.buttonLabel}
                                                </span>
                                            )}

                                            {/* Message text with WhatsApp formatting */}
                                            <p className="text-gray-800 whitespace-pre-wrap break-words">
                                                {renderWhatsAppText(entry.text)}
                                            </p>

                                            {/* Quick-reply buttons (bot only) */}
                                            {!isUser && entry.buttons && entry.buttons.length > 0 && (
                                                <div className="flex flex-col gap-1.5 mt-2.5 pt-2 border-t border-gray-100">
                                                    {entry.buttons.map((btn, bIdx) => (
                                                        <button
                                                            key={bIdx}
                                                            onClick={() => handleButtonClick(btn)}
                                                            disabled={sending}
                                                            className="flex items-center justify-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg border transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-50 active:scale-95"
                                                            style={{
                                                                color: '#128C7E',
                                                                borderColor: 'rgba(18,140,126,0.35)',
                                                                background: 'rgba(18,140,126,0.05)',
                                                            }}
                                                        >
                                                            <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 opacity-70">
                                                                <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clipRule="evenodd" />
                                                            </svg>
                                                            {btn.title}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}

                                            {/* WhatsApp List Message CTA (bot only) */}
                                            {!isUser && entry.sections && entry.sections.length > 0 && (
                                                <div className="mt-2.5 pt-2 border-t border-gray-100">
                                                    <button
                                                        onClick={() => setOpenListIdx(openListIdx === idx ? -1 : idx)}
                                                        disabled={sending}
                                                        className="w-full flex items-center justify-center gap-2 text-[13px] font-semibold py-1.5 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-50 active:scale-95"
                                                        style={{ color: '#128C7E' }}
                                                    >
                                                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                                                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                                        </svg>
                                                        {entry.listButton || 'Select'}
                                                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 opacity-60" style={{ transform: openListIdx === idx ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                                                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>

                                                    {/* Inline picker sheet */}
                                                    {openListIdx === idx && (
                                                        <div className="mt-2 rounded-xl overflow-hidden border border-gray-200 shadow-md bg-white">
                                                            <div className="px-3 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest bg-gray-50 border-b border-gray-100">
                                                                Select an option
                                                            </div>
                                                            {entry.sections.map((sec, sIdx) => (
                                                                <div key={sIdx}>
                                                                    {sec.title && (
                                                                        <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
                                                                            {sec.title}
                                                                        </div>
                                                                    )}
                                                                    {sec.rows.map((row, rIdx) => (
                                                                        <button
                                                                            key={rIdx}
                                                                            onClick={() => handleListRowClick(row)}
                                                                            className="w-full text-left px-3 py-2.5 hover:bg-green-50 transition-colors border-b border-gray-50 last:border-0 flex flex-col gap-0.5"
                                                                        >
                                                                            <span className="text-[13px] font-medium text-gray-800">{row.title}</span>
                                                                            {row.description && (
                                                                                <span className="text-[11px] text-gray-400">{row.description}</span>
                                                                            )}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Timestamp + read receipt */}
                                            <div className="flex items-center gap-0.5 justify-end mt-1">
                                                <span className="text-[10px] text-gray-400">{formatTime(entry.timestamp)}</span>
                                                {isUser && <DoubleTickIcon />}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Bot typing indicator */}
                            {sending && (
                                <div className="flex justify-start mb-0.5">
                                    <div
                                        className="px-4 py-3 rounded-lg shadow-sm"
                                        style={{ background: '#FFFFFF', borderRadius: '2px 12px 12px 12px' }}
                                    >
                                        <div className="flex items-center gap-1">
                                            {[0, 150, 300].map(d => (
                                                <span
                                                    key={d}
                                                    className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
                                                    style={{ animationDelay: `${d}ms` }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div ref={bottomRef} />
                        </>
                    )}
                </div>

                {/* ── Input bar ── */}
                <div
                    className="flex items-center gap-2 px-3 py-2.5 flex-shrink-0"
                    style={{ background: '#F0F0F0', borderTop: '1px solid #ddd' }}
                >
                    {!sessionStarted ? (
                        <div className="flex-1 bg-white/60 rounded-full px-4 py-2 text-[12px] text-gray-400 select-none">
                            Start a session to chat…
                        </div>
                    ) : (
                        <>
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder="Type a message…"
                                value={inputText}
                                onChange={e => setInputText(e.target.value)}
                                onKeyDown={handleKeyDown}
                                disabled={sending}
                                className="flex-1 bg-white rounded-full px-4 py-2 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-60"
                                style={{ border: '1px solid #e0e0e0' }}
                            />
                            <button
                                onClick={handleSendText}
                                disabled={!inputText.trim() || sending}
                                className="w-10 h-10 rounded-full flex items-center justify-center text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 flex-shrink-0"
                                style={{ background: inputText.trim() && !sending ? '#25D366' : '#a8d5b5' }}
                                title="Send"
                            >
                                <SendIcon />
                            </button>
                        </>
                    )}
                </div>

                {/* ── Error toast (if any non-bubble error) ── */}
                {error && (
                    <div className="px-3 py-2 text-[11px] text-red-600 bg-red-50 border-t border-red-100 flex items-center gap-1.5 flex-shrink-0">
                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 flex-shrink-0">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {error}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RfqCareSimulator;
